import { Play } from 'lucide-react';

export interface TranscriptTabProps {
    transcript?: string | null;
    durationLabel?: string;
}

interface ParsedTurn {
    speaker: string;
    text: string;
}

function parseTranscript(transcript: string): ParsedTurn[] {
    return transcript
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
            const match = line.match(/^([A-Za-z ]+):\s*(.*)$/);
            if (match) return { speaker: match[1], text: match[2] };
            return { speaker: 'Speaker', text: line };
        });
}

export function TranscriptTab({ transcript, durationLabel }: TranscriptTabProps) {
    if (!transcript) {
        return (
            <div className="px-5 py-12 text-center text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                <Play
                    size={20}
                    strokeWidth={1.5}
                    style={{ display: 'inline', marginBottom: 8, color: 'var(--text-quaternary)' }}
                />
                <div>No transcript available for this candidate yet.</div>
            </div>
        );
    }

    const turns = parseTranscript(transcript);

    return (
        <div className="flex flex-col">
            <div
                className="px-5 py-4 flex items-center justify-between"
                style={{ borderBottom: '1px solid var(--border-muted)' }}
            >
                <div>
                    <div className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                        Interview transcript
                    </div>
                    {durationLabel && (
                        <div className="text-[12.5px] mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                            Duration · {durationLabel}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    className="flex items-center gap-1.5 h-8 px-3.5 rounded-full text-[12.5px] font-medium"
                    style={{ background: 'var(--accent)', color: 'white' }}
                >
                    <Play size={12} strokeWidth={2} fill="white" />
                    Play recording
                </button>
            </div>

            <div className="px-5 py-3">
                {turns.map((turn, i) => {
                    const isInterviewer = /interviewer|recruiter|ai/i.test(turn.speaker);
                    return (
                        <div
                            key={i}
                            className="py-3"
                            style={{ borderBottom: i < turns.length - 1 ? '1px solid var(--border-muted)' : 'none' }}
                        >
                            <div className="flex items-center gap-2 mb-1.5">
                                <span
                                    className="text-[11.5px] font-semibold uppercase tracking-[0.06em]"
                                    style={{
                                        color: isInterviewer ? 'var(--accent)' : 'var(--fg-info)',
                                    }}
                                >
                                    {turn.speaker}
                                </span>
                            </div>
                            <p
                                className="text-[13.5px] leading-relaxed"
                                style={{ color: 'var(--text-primary)' }}
                            >
                                {turn.text}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
